import {
  Box,
  IconButton,
  Link,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import StarRating from '../StarRating/StarRating';
import GrowthIndicator from '../GrowthIndicator/GrowthIndicator';
import { MoreVert } from '@mui/icons-material';
import BenchmarkDataCell from '../BenchmarkDataCell/BenchmarkDataCell';
import Chip from '../Chip/Chip';
import { ICompetitor } from '../../shared/interfaces/ICompetitor';
import ScoreService from '../../services/ScoreService';
import { IScoreEntry } from '../../shared/interfaces/IScoreEntry';
import { useTranslation } from 'react-i18next';
import BenchmarkingService from '../../services/BenchmarkingService';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import Confirm from '../Confirm/Confirm';

interface Props extends ICompetitor {
  platform: string;
  index: number;
  isSubject: boolean;
  sx?: SxProps<Theme>;
  benchmarkID?: string | undefined;
  competitorID?: string | undefined;
  reload: () => Promise<void>;
}

const BenchmarkCompetitor: FC<Props> = ({
  label,
  company,
  url,
  platform,
  index,
  isSubject,
  sx,
  benchmarkID,
  competitorID,
  reload,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [scoreData, setScoreData] = useState<IScoreEntry | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (platform === 'ärzte') {
    platform = 'aerzte';
  }
  if (platform === 'mobile.de') {
    platform = 'mobile';
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget as any);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const deleteCompetitor = async () => {
    if (benchmarkID && competitorID) {
      await ReviewMonitoringService.deleteMonitoring(competitorID);
      await BenchmarkingService.deleteBenchmarkEntry(benchmarkID, competitorID);
      await reload();
      handleClose();
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const getScoreData = async () => {
      const data = await ScoreService.getLastTotalScore(
        platform,
        company,
        setLoading,
        controller.signal
      );

      if (!data) return;

      setScoreData(data);
    };

    (async () => await getScoreData())();

    return () => {
      controller.abort();
    };
  }, [company, platform]);

  const borderProps: SxProps<Theme> = {
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
        borderRadius: theme.spacing(2),
        px: theme.spacing(2),
        ...sx,
      }}
    >
      <BenchmarkDataCell
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: 'center',
        }}
      >
        <Chip
          label={`${index}`}
          sx={{
            backgroundColor: index === 1 ? theme.palette.primary.main : theme.palette.divider,
            color: index === 1 ? '#f1f1f1' : theme.palette.text.primary,
          }}
        />
        <Link
          href={url}
          variant="body1"
          underline="none"
          target="_blank"
          sx={{
            fontWeight: isSubject ? 700 : 400,
          }}
        >
          {label.slice(0, 18)}
        </Link>
        {!isSubject && (
          <Box>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleClose}
            >
              <MenuItem onClick={() => setDeleteIsOpen(true)}>{t('delete')}</MenuItem>
            </Menu>
            <Confirm
              text={t('delete_confirm')}
              open={deleteIsOpen}
              onCancel={() => {
                setDeleteIsOpen(false);
                handleClose();
              }}
              onOk={async () => await deleteCompetitor()}
            />
          </Box>
        )}
      </BenchmarkDataCell>
      <BenchmarkDataCell sx={borderProps}>
        {scoreData ? (
          <>
            <Typography variant="body1">{scoreData.score.score}</Typography>
            <StarRating
              value={scoreData.score.score}
              sx={{ ml: theme.spacing(1) }}
            />
          </>
        ) : (
          <Typography variant="body1">N/A</Typography>
        )}
      </BenchmarkDataCell>
      <BenchmarkDataCell sx={borderProps}>
        {scoreData ? (
          <Typography variant="body1">{scoreData.score.reviewCount}</Typography>
        ) : (
          <Typography variant="body1">N/A</Typography>
        )}
      </BenchmarkDataCell>
      <BenchmarkDataCell sx={borderProps}>
        {scoreData ? (
          <>
            <Typography variant="body1">{scoreData.reviewsWithComment}</Typography>
          </>
        ) : (
          <Typography variant="body1">N/A</Typography>
        )}
      </BenchmarkDataCell>
      <BenchmarkDataCell sx={borderProps}>
        {scoreData ? (
          <>
            <Typography
              variant="body1"
              sx={{ mr: theme.spacing(1) }}
            >
              {scoreData.growthWeekPercentage}%
            </Typography>
            <GrowthIndicator growth={scoreData.growthWeekPercentage} />{' '}
          </>
        ) : (
          <Typography variant="body1">N/A</Typography>
        )}
      </BenchmarkDataCell>
      <BenchmarkDataCell sx={borderProps}>
        {scoreData ? (
          <>
            <Typography
              variant="body1"
              sx={{ mr: theme.spacing(1) }}
            >
              {scoreData.growthMonthPercentage}%
            </Typography>
            <GrowthIndicator growth={scoreData.growthMonthPercentage} />
          </>
        ) : (
          <Typography variant="body1">N/A</Typography>
        )}
      </BenchmarkDataCell>
      <BenchmarkDataCell>
        {scoreData ? (
          <>
            <Tooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{t('percentage_score_ytd')}</Typography>
                </Box>
              }
              arrow
            >
              <Typography
                variant="body1"
                sx={{ mr: theme.spacing(1) }}
              >
                {scoreData.growthPercentage}%
              </Typography>
            </Tooltip>
            <GrowthIndicator growth={scoreData.growthPercentage} />
          </>
        ) : (
          <Typography variant="body1">N/A</Typography>
        )}
      </BenchmarkDataCell>
    </Box>
  );
};

export default BenchmarkCompetitor;
